import { Component, Input, OnInit } from '@angular/core';
import { AlertController, PopoverController, ModalController, ActionSheetController } from '@ionic/angular';
import { EventsService } from '../../_services/events.service';
import { Breadcrumb } from '../../_interfaces/header.interfaces';
import { AuthService } from '../../_services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { HelpPopoverComponent } from '../help-popover/help-popover.component';
import { ModuleEnum } from '../../_utils/unums/module.enum';
import { ComponentBase } from '../../_utils/component.base';
import { ChatModalComponent } from '../chat-modal/chat-modal.component';
import { NotificationsModalComponent } from '../notifications-modal/notifications-modal.component';
import { FirestoreService } from '../../_services/firestore.service';
import { SettingsService } from '../../_services/settings.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})

export class HeaderComponent extends ComponentBase implements OnInit {
  user;
  firstName;
  notifications = 1;

  alertTitle = 'Are you sure you want to log out?';
  alertOkButon = 'Logout';
  alertCancelButton = 'Cancel';

  @Input() breadcrumbs: Breadcrumb[];
  @Input() showIcon = false;
  @Input() stopGoToProfile = false;
  @Input() helpButtonName = 'help-button';
  @Input() public isVisibleHelp: boolean;
  @Input() public isVisibleWebsite: boolean;
  @Input() public module: ModuleEnum;

  isVisibleForum = false;
  isVisibleChat = false;

  get parentBreadcrumb(): Breadcrumb | null {
    if (!this.breadcrumbs || this.breadcrumbs.length < 2) return null;
    return this.breadcrumbs[this.breadcrumbs.length - 2];
  }

  newForumMessages = false;
  newChatMessages = false;
  newMotificationsMessages = true;

  env;
  isOpen = false;

  constructor(
    public authService: AuthService,
    private event: EventsService,
    public translate: TranslateService,
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private modalCtrl: ModalController,
    private firestoreService: FirestoreService,
    private popoverController: PopoverController,
    private settingsService: SettingsService) {
    super();
    this.initVariables();
    this.env = environment.name;
  }

  async ngOnInit() {
    this.helpButtonName = this.router.url.replace(/\//g, '')
    this.user = await this.authService.getUser();
    if (this.user) this.firstName = this.user.displayName?.split(' ')[0];

    this.event.getObservable().subscribe(res => {
      if (res && res.displayName) {
        this.user = res
      }
    });

    if (this.isVisibleHelp) {
      const moduleSetting = await this.settingsService.getHeaderSettings();
      if (moduleSetting?.showForum) this.isVisibleForum = true;
      if (moduleSetting?.showChat) {

        this.isVisibleChat = true;
        this.firestoreService.getUnreadMessages(this.user.uid).subscribe((count: number | undefined) => {
          const unreadMssg = count ? count : 0;
          if (unreadMssg > 0) {
            this.newChatMessages = true;
          }
        });
      }
    }
  }


  goToSimskillsPage() {
    window.open('http://simskills.io/', '_blank');
  }

  goToProfilePage() {
    if (this.stopGoToProfile) return
    else this.router.navigateByUrl('/profile');
  }

  goToBreadcrumb(url: string) {
    if (url) {
      this.router.navigateByUrl(url).then();
    }
  }

  async presentLogoutAlert() {
    this.alertTitle = this.translate.instant(this.alertTitle);
    this.alertOkButon = this.translate.instant(this.alertOkButon);
    this.alertCancelButton = this.translate.instant(this.alertCancelButton);

    const alert = await this.alertController.create({
      header: this.alertTitle,
      buttons: [
        {
          text: this.alertCancelButton,
          role: 'cancel',
        },
        {
          text: this.alertOkButon,
          role: 'confirm',
          handler: async () => {
            this.authService.logout();
            this.router.navigateByUrl('/login');
          },
        },
      ],
    });

    await alert.present();
  }

  async openHelpPopover() {
    const popover = await this.popoverController.create({
      component: HelpPopoverComponent,
      cssClass: 'help-popover',
      mode: 'ios',
      side: 'bottom',
      alignment: 'center',
      arrow: true,
      trigger: this.helpButtonName,
    });

    await popover.present();
  }

  async openMobileMenu() {
    const buttons: any[] = [];

    if (this.isVisibleForum) {
      buttons.push({
        text: this.translate.instant('Go to forum'),
        icon: 'people-outline',
        handler: () => {
          this.openForum();
        }
      });
    }

    if (this.isVisibleChat) {
      buttons.push({
        text: this.translate.instant('Open chat'),
        icon: 'chatbubbles-outline',
        cssClass: this.newChatMessages ? 'chat-with-badge' : '',
        handler: () => {
          this.openChatModal();
        }
      });
    }

    if (this.isVisibleHelp) {
      buttons.push({
        text: this.translate.instant('Help'),
        icon: 'help-circle-outline',
        handler: () => {
          this.openHelpPopover();
        }
      });
    }

    if (this.isVisibleWebsite) {
      buttons.push({
        text: this.translate.instant('Go to website'),
        icon: 'globe',
        handler: () => {
          this.goToSimskillsPage();
        }
      });
    }

    if (this.showIcon) {
      buttons.push({
        text: this.translate.instant('Logout'),
        icon: 'power',
        role: 'destructive',
        handler: () => {
          this.presentLogoutAlert();
        }
      });
    }

    buttons.push({
      text: this.translate.instant('Cancel'),
      role: 'cancel',
      icon: 'close'
    });

    const actionSheet = await this.actionSheetController.create({
      header: this.translate.instant('Menu'),
      buttons: buttons,
      cssClass: 'mobile-header-menu'
    });

    await actionSheet.present();
  }

  async openChatModal() {
    const modal = await this.modalCtrl.create({
      component: ChatModalComponent,
      cssClass: 'chat-modal',
      backdropDismiss: true,
      keyboardClose: false
    });

    modal.onDidDismiss().then((data) => {
      if (data && data.data && data.data.showNewChatNotif !== undefined) {
        this.newChatMessages = data.data.showNewChatNotif;
      }
    });


    return await modal.present();
  }

  async openNotificationsModal() {
    const modal = await this.modalCtrl.create({
      component: NotificationsModalComponent,
      cssClass: 'notifications-modal'
    });

    return await modal.present();
  }

  async openForum() {
    window.open('/forum', '_blank');
  }

  private initVariables(): void {
    this.module = this.activatedRoute.snapshot.params.module;
    this.isVisibleHelp = true;
    this.isVisibleWebsite = true;
  }

}
